import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
// import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import LogoAnLangOnlyLayout from '../layouts/LogoAnLangOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// components
import LoadingLogoScreen from '../components-custom/LoadingLogoScreen';
//
import { PATH } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingLogoScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Dashboard Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH.overview} replace />, index: true },
        { path: 'overview', element: <Overview /> },
        {
          path: 'business',
          children: [
            { element: <Business />, index: true },
            { path: ':id', element: <BusinessDetail /> },
          ],
        },
        {
          path: 'distributor',
          children: [
            { element: <Navigate to={PATH.distributor.distributor.root} replace />, index: true },
            {
              path: 'distributor',
              children: [
                { element: <Distributor />, index: true },
                { path: ':id', element: <DistributorDetail /> },
              ],
            },
            { path: 'commission', element: <Commission /> },
          ],
        },
        {
          path: 'explore',
          children: [
            { element: <Navigate to={PATH.explore.overview} replace />, index: true },
            { path: 'overview', element: <ExploreOverview /> },
            {
              path: 'record',
              children: [
                { element: <ExploreRecord />, index: true },
                { path: ':id', element: <ExploreRecordDetail /> },
              ],
            },
            { path: 'report', element: <ExploreReport /> },
          ],
        },
        {
          path: 'project',
          children: [
            { element: <Navigate to={PATH.project.root} replace />, index: true },
            { path: 'board', element: <ProjectBoard /> },
          ],
        },
        {
          path: 'support',
          children: [
            { element: <Support />, index: true },
            { path: ':id', element: <SupportDetail /> },
            { path: 'settings', element: <SupportSettings /> },
          ],
        },
        {
          path: 'management',
          children: [
            { element: <Navigate to={PATH.management.settings.root} replace />, index: true },
            { path: 'settings', element: <Settings /> },
            {
              path: 'user',
              children: [
                { element: <User />, index: true },
                { path: ':id', element: <UserDetail /> },
              ],
            },
            {
              path: 'role',
              children: [
                { element: <Role />, index: true },
                { path: ':id', element: <RoleDetail /> },
              ],
            },
            {
              path: 'tax',
              children: [
                { element: <Tax />, index: true },
                { path: ':id', element: <TaxDetail /> },
              ],
            },
            {
              path: 'plan',
              children: [
                { element: <Plan />, index: true },
                { path: ':id', element: <PlanDetail /> },
              ],
            },
            {
              path: 'discount',
              children: [
                { element: <Discount />, index: true },
                { path: ':id', element: <DiscountDetail /> },
              ],
            },
            {
              path: 'category-expense',
              children: [
                { element: <CategoryExpense />, index: true },
                { path: ':id', element: <CategoryExpenseDetail /> },
              ],
            },
            {
              path: 'uom',
              children: [
                { element: <Uom />, index: true },
                { path: ':id', element: <UomDetail /> },
              ],
            },
            {
              path: 'nearpay-terminal',
              children: [
                { element: <NearpayTerminal />, index: true },
                { path: ':id', element: <NearpayTerminalDetail /> },
              ],
            },
          ],
        },
      ],
    },

    // Auth Routes
    {
      path: '/',
      element: (
        <GuestGuard>
          <LogoOnlyLayout />
        </GuestGuard>
      ),
      children: [
        { path: 'login', element: <Login /> },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: 'reset-password/:code', element: <ResetPassword /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoAnLangOnlyLayout />,
      children: [
        { path: 'suspend', element: <Suspend /> },
        { path: '403', element: <Page403 /> },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to={PATH.misc.page404} replace /> },
      ],
    },
    { path: '*', element: <Navigate to={PATH.misc.page404} replace /> },
  ]);
}

// IMPORT COMPONENTS

// Dashboard
const Overview = Loadable(lazy(() => import('../pages/overview/Overview')));

const ProjectBoard = Loadable(lazy(() => import('../pages/project/ProjectBoard')));

const Business = Loadable(lazy(() => import('../pages/business/Business')));
const BusinessDetail = Loadable(lazy(() => import('../pages/business/BusinessDetail')));

const Distributor = Loadable(lazy(() => import('../pages/distributor/distributor/Distributor')));
const DistributorDetail = Loadable(lazy(() => import('../pages/distributor/distributor/DistributorDetail')));
const Commission = Loadable(lazy(() => import('../pages/distributor/commission/Commission')));

const Settings = Loadable(lazy(() => import('../pages/management/settings/Settings')));
const User = Loadable(lazy(() => import('../pages/management/user/User')));
const UserDetail = Loadable(lazy(() => import('../pages/management/user/UserDetail')));
const Role = Loadable(lazy(() => import('../pages/management/role/Role')));
const RoleDetail = Loadable(lazy(() => import('../pages/management/role/RoleDetail')));
const Tax = Loadable(lazy(() => import('../pages/management/tax/Tax')));
const TaxDetail = Loadable(lazy(() => import('../pages/management/tax/TaxDetail')));
const Plan = Loadable(lazy(() => import('../pages/management/plan/Plan')));
const PlanDetail = Loadable(lazy(() => import('../pages/management/plan/PlanDetail')));
const Discount = Loadable(lazy(() => import('../pages/management/discount/Discount')));
const DiscountDetail = Loadable(lazy(() => import('../pages/management/discount/DiscountDetail')));

const CategoryExpense = Loadable(lazy(() => import('../pages/management/categoryExpense/CategoryExpense')));
const CategoryExpenseDetail = Loadable(lazy(() => import('../pages/management/categoryExpense/CategoryExpenseDetail')));

const Uom = Loadable(lazy(() => import('../pages/management/uom/Uom')));
const UomDetail = Loadable(lazy(() => import('../pages/management/uom/UomDetail')));

const NearpayTerminal = Loadable(lazy(() => import('../pages/management/nearpayTerminal/NearpayTerminal')));
const NearpayTerminalDetail = Loadable(lazy(() => import('../pages/management/nearpayTerminal/NearpayTerminalDetail')));

// Support
const Support = Loadable(lazy(() => import('../pages/support/Support')));
const SupportDetail = Loadable(lazy(() => import('../pages/support/SupportDetail')));
const SupportSettings = Loadable(lazy(() => import('../pages/support/SupportSettings')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));

const Page403 = Loadable(lazy(() => import('../pages/misc/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/misc/Page404')));
const Suspend = Loadable(lazy(() => import('../pages/misc/Suspend')));

// Explore
const ExploreOverview = Loadable(lazy(() => import('../pages/explore/overview/Overview')));
const ExploreRecord = Loadable(lazy(() => import('../pages/explore/record/Record')));
const ExploreRecordDetail = Loadable(lazy(() => import('../pages/explore/record/RecordDetail')));
const ExploreReport = Loadable(lazy(() => import('../pages/explore/report/Report')));
